import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Plus, Play, ArrowRight, FolderTree, Clock, Database, FileStack, Activity, Crown } from "lucide-react";
import ProviderIcon from "@/components/ProviderIcon";
import { toast } from "sonner";

function formatBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function relTime(iso, fallback) {
  if (!iso) return fallback;
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

function absTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function StatusBadge({ status }) {
  const map = {
    idle: { cls: "bg-muted text-muted-foreground", label: "IDLE" },
    running: { cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400", label: "RUNNING" },
    success: { cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", label: "SUCCESS" },
    partial: { cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400", label: "PARTIAL" },
    failed: { cls: "bg-destructive/15 text-destructive", label: "FAILED" },
  };
  const s = map[status] || map.idle;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 font-mono text-[10px] ${s.cls}`}>
      <span className={`h-1.5 w-1.5 ${status === "running" ? "animate-pulse" : ""} bg-current`} />
      {s.label}
    </span>
  );
}

function JobCard({ job, onRun }) {
  const { t } = useI18n();
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    setRunning(true);
    try {
      await api.post(`/jobs/${job.id}/run`);
      toast.success("Backup started");
      onRun?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="group border border-border bg-card p-5 transition hover:border-foreground/30">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-display text-lg font-bold tracking-tight">{job.name}</h3>
            <StatusBadge status={job.status} />
          </div>
          <div className="mt-1 font-mono text-[11px] text-muted-foreground">id::{job.id.slice(0, 8)}</div>
        </div>
        {!job.enabled && (
          <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
            {t("common.disabled")}
          </Badge>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        <EndpointRow
          label="src"
          provider={job.source_account_provider}
          email={job.source_account_email}
          folder={job.source_folder_name || "root"}
        />
        <EndpointRow
          label="dst"
          provider={job.dest_account_provider}
          email={job.dest_account_email}
          folder={job.dest_folder_name || "root"}
        />
        <Row label="freq" value={(job.schedule?.frequency || "manual").toUpperCase()} />
        <Row
          label="next"
          value={
            job.next_run_at
              ? new Date(job.next_run_at).toLocaleString()
              : job.schedule?.frequency === "manual"
              ? t("dashboard.onDemand")
              : "—"
          }
        />
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
        <div className="font-mono text-[11px] text-muted-foreground">
          <span className="text-foreground">{job.files_total || 0}</span> files ·{" "}
          <span className="text-foreground">{formatBytes(job.bytes_total || 0)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm h-8 gap-1"
            onClick={handleRun}
            disabled={running}
            data-testid={`job-run-${job.id}`}
          >
            <Play className="h-3.5 w-3.5" /> {t("dashboard.runNow")}
          </Button>
          <Button asChild size="sm" className="rounded-sm h-8 bg-foreground text-background hover:bg-foreground/90 gap-1" data-testid={`job-view-${job.id}`}>
            <Link to={`/jobs/${job.id}`}>
              {t("dashboard.viewDetails")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center gap-2 truncate">
      <span className="font-mono text-[10px] uppercase text-muted-foreground">{label}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

function EndpointRow({ label, provider, email, folder }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="font-mono text-[10px] uppercase text-muted-foreground shrink-0">{label}</span>
      {provider ? (
        <ProviderIcon
          provider={provider}
          className="h-3.5 w-3.5 shrink-0"
          aria-label={provider === "microsoft" ? "OneDrive" : "Google Drive"}
        />
      ) : null}
      <span className="min-w-0 truncate" title={email ? `${email} · ${folder}` : folder}>
        {email ? (
          <>
            <span className="text-muted-foreground">{email}</span>
            <span className="mx-1 text-muted-foreground/60">/</span>
            <span>{folder}</span>
          </>
        ) : (
          folder
        )}
      </span>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="mt-3 font-display text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function PlanUsageCard({ billing }) {
  const { t } = useI18n();
  const pct = billing.unlimited ? 0 : Math.min(100, billing.percent_used || 0);
  const isPaid = billing.plan !== "free";
  const usedGb = (billing.used_bytes / 1024 ** 3).toFixed(2);
  const totalGb = billing.quota_bytes ? (billing.quota_bytes / 1024 ** 3).toFixed(0) : null;

  return (
    <div className="mt-8 border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`flex h-12 w-12 items-center justify-center border border-border ${isPaid ? "bg-[#002FA7] text-white" : "bg-background"}`}>
            <Crown className="h-5 w-5" />
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase text-muted-foreground">{t("pricing.planLabel")}</div>
            <div className="font-display text-2xl font-black tracking-tight">{billing.plan_label}</div>
          </div>
        </div>

        <div className="flex-1 min-w-[220px] max-w-md">
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono uppercase text-muted-foreground">{t("pricing.usage")}</span>
            <span className="font-mono">
              {billing.unlimited
                ? t("pricing.unlimited")
                : `${usedGb} GB ${t("pricing.usageOf")} ${totalGb} GB`}
            </span>
          </div>
          <div className="mt-2 h-2 w-full bg-muted">
            <div
              className={`h-full transition-all ${pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-amber-500" : "bg-[#002FA7]"}`}
              style={{ width: billing.unlimited ? "100%" : `${pct}%`, backgroundImage: billing.unlimited ? "repeating-linear-gradient(45deg, #002FA7 0 8px, #0049ff 8px 16px)" : undefined }}
            />
          </div>
          {billing.plan_expires_at && (
            <div className="mt-2 font-mono text-[10px] text-muted-foreground">
              {t("pricing.renewsOn")}: {new Date(billing.plan_expires_at).toLocaleDateString()}
            </div>
          )}
        </div>

        <Link
          to="/pricing"
          className="rounded-sm bg-foreground px-4 py-2 font-mono text-xs uppercase text-background hover:bg-foreground/90 transition"
          data-testid="dashboard-upgrade-button"
        >
          {isPaid ? t("pricing.manage") : t("pricing.upgradeNow")}
        </Link>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const [jobs, setJobs] = useState(null);
  const [stats, setStats] = useState(null);
  const [billing, setBilling] = useState(null);
  // Paginated recent activity (separate from /stats/overview so changing page
  // doesn't recompute the heavy aggregates above).
  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  const load = async () => {
    const [j, s, b, r] = await Promise.all([
      api.get("/jobs"),
      api.get("/stats/overview"),
      api.get("/billing/me").catch(() => ({ data: null })),
      api.get("/stats/recent-runs", { params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE } }),
    ]);
    setJobs(j.data);
    setStats(s.data);
    setBilling(b.data);
    setRuns(r.data.items || []);
    setRunsTotal(r.data.total || 0);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <div className="App min-h-screen bg-background">
      <Header variant="app" />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// Dashboard</div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
              {t("dashboard.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
          </div>
          <Button asChild className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid="new-job-button">
            <Link to="/jobs/new">
              <Plus className="h-4 w-4" /> {t("dashboard.newJob")}
            </Link>
          </Button>
        </div>

        {/* Plan / Usage */}
        {billing && <PlanUsageCard billing={billing} />}

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label={t("dashboard.jobs")} value={stats?.jobs_total ?? "—"} icon={Database} />
          <StatCard label={t("dashboard.activeJobs")} value={stats?.jobs_active ?? "—"} icon={Activity} />
          <StatCard label={t("dashboard.accounts")} value={stats?.accounts_total ?? "—"} icon={FolderTree} />
          <StatCard
            label={t("dashboard.totalData")}
            value={stats ? formatBytes(stats.total_bytes) : "—"}
            icon={FileStack}
          />
        </div>

        {/* Jobs list */}
        <section className="mt-10">
          <h2 className="font-display text-xl font-bold tracking-tight">{t("nav.jobs")}</h2>
          {jobs === null && (
            <div className="mt-6 font-mono text-sm text-muted-foreground">{t("common.loading")}</div>
          )}
          {jobs && jobs.length === 0 && (
            <div className="mt-6 border border-dashed border-border bg-card p-10 text-center">
              <FolderTree className="mx-auto h-8 w-8 text-muted-foreground" />
              <h3 className="mt-4 font-display text-lg font-bold">{t("dashboard.noJobs")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.noJobsSub")}</p>
              <Button asChild className="mt-5 rounded-sm bg-[#002FA7] text-white hover:bg-[#002FA7]/90" data-testid="empty-new-job">
                <Link to="/jobs/new">
                  <Plus className="mr-2 h-4 w-4" /> {t("dashboard.newJob")}
                </Link>
              </Button>
            </div>
          )}
          {jobs && jobs.length > 0 && (
            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {jobs.map((j) => (
                <JobCard key={j.id} job={j} onRun={load} />
              ))}
            </div>
          )}
        </section>

        {/* Recent activity */}
        <section className="mt-10">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-xl font-bold tracking-tight">{t("dashboard.recentActivity")}</h2>
            {runsTotal > 0 && (
              <span className="font-mono text-[11px] text-muted-foreground">
                {Math.min(page * PAGE_SIZE + 1, runsTotal)}–{Math.min((page + 1) * PAGE_SIZE, runsTotal)} / {runsTotal}
              </span>
            )}
          </div>
          <div className="mt-4 border border-border bg-card">
            {!runs.length && (
              <div className="p-6 font-mono text-sm text-muted-foreground">{t("dashboard.noActivity")}</div>
            )}
            {runs.map((r) => {
              const job = jobs?.find((j) => j.id === r.job_id);
              return (
                <div key={r.id} className="flex items-center justify-between border-b border-border px-4 py-3 last:border-0 text-sm">
                  <div className="flex items-center gap-3">
                    <StatusBadge status={r.status} />
                    <span className="font-mono text-xs">{job?.name || r.job_id.slice(0, 8)}</span>
                  </div>
                  <div className="flex items-center gap-4 font-mono text-xs text-muted-foreground">
                    <span>{r.files_copied} {t("common.copied").toLowerCase()}</span>
                    <span>{formatBytes(r.bytes_transferred || 0)}</span>
                    <span className="hidden sm:inline">{relTime(r.started_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {runsTotal > PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="activity-prev-page"
              >
                {t("common.previous")}
              </Button>
              <span className="font-mono text-[11px] text-muted-foreground">
                {t("common.page")} {page + 1} / {Math.max(1, Math.ceil(runsTotal / PAGE_SIZE))}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= runsTotal}
                data-testid="activity-next-page"
              >
                {t("common.next")}
              </Button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
